<template>
  <div v-if="slot" ref="menu" class="w-room-menu" aria-label="Выбор кабинета">
    <div class="w-menu-heading">
      <strong>Кабинет на {{ slot.start }}–{{ slot.end }}</strong>
      <span class="w-small">{{ formatDate(slot.date, { day: 'numeric', month: 'long' }) }}</span>
    </div>
    <button
      v-for="option in options"
      :key="option.room.id"
      type="button"
      class="w-room-option"
      :class="{ 'is-selected': option.current }"
      :data-color="option.color"
      :disabled="option.disabled"
      :aria-label="option.ariaLabel"
      @click="store.changeRoom(option.room.id)"
    >
      <span class="w-option-title">
        <strong>{{ option.number }}</strong>
        <span>{{ option.room.purpose }}</span>
        <q-icon v-if="option.current" name="check" />
      </span>
      <span class="w-option-sub">{{ option.description }}</span>
    </button>
    <div v-if="hasPatient" class="w-menu-note">
      Для смены кабинета сначала отмените запись пациента.
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed, nextTick, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { useScheduleStore } from '@/store/calendar/scheduleModule';
import { formatDate } from '~utils/calendarModel';
import type { Availability } from '@/store/calendar/types';

type RoomMenuProps = {
  container: HTMLElement | null;
};

const MENU_MAX_WIDTH = 300;
const MENU_GAP = 7;
const EDGE_GAP = 10;

const props = defineProps<RoomMenuProps>();

const store = useScheduleStore();
const menu = ref<HTMLElement | null>(null);

const slot = computed(() => (
  store.schedule.slots.find((item) => item.id === store.roomMenuSlotId) || null
));

const hasPatient = computed(() => (
  store.schedule.appointments.some((item) => item.slotId === slot.value?.id)
));

const describe = (availability: Availability): string => {
  if (availability.color === 'red') return 'Есть запись пациента';
  if (availability.branchBlocked) return availability.branchReason;
  if (availability.color === 'gray') {
    return `Закрыт: ${availability.roomBreaks[0]?.label || 'перерыв'}`;
  }
  if (availability.color === 'yellow') {
    return `${availability.staffIds.map(store.lastName).join(', ')} · без пациентов`;
  }
  return 'Свободен для вас';
};

const options = computed(() => {
  const current = slot.value;
  if (!current) return [];
  return store.allowedRooms.map((room) => {
    const availability = store.slotAvailability(current, room.id);
    const isCurrent = room.id === current.roomId;
    const number = room.number ?? room.id;
    const description = describe(availability);
    return {
      room,
      number,
      current: isCurrent,
      color: availability.color,
      description,
      ariaLabel: `Кабинет ${number}. ${description}${isCurrent ? '. Выбран' : ''}`,
      // Занять другой кабинет нельзя, если он занят или на приём уже записан пациент
      disabled: !store.canEdit || (!isCurrent && (availability.blocked || hasPatient.value)),
    };
  });
});

// Меню открывается под кнопкой кабинета, а если снизу не помещается — над ней
const placeMenu = () => {
  const element = menu.value;
  const anchor = props.container?.querySelector(`[data-room-slot="${store.roomMenuSlotId}"]`);
  if (!element || !props.container || !anchor) return;
  const box = props.container.getBoundingClientRect();
  const button = anchor.getBoundingClientRect();
  element.style.width = `${Math.min(MENU_MAX_WIDTH, box.width - 24)}px`;
  const { offsetWidth: width, offsetHeight: height } = element;
  const below = button.bottom - box.top + MENU_GAP;
  const above = button.top - box.top - height - MENU_GAP;
  const left = Math.min(button.left - box.left, box.width - width - EDGE_GAP);
  element.style.left = `${Math.max(EDGE_GAP, left)}px`;
  element.style.top = `${Math.max(8, below + height < box.height ? below : above)}px`;
};

watch(() => store.roomMenuSlotId, async (id) => {
  if (id == null) return;
  await nextTick();
  placeMenu();
});

const closeOnOutsideClick = (event: PointerEvent) => {
  if (store.roomMenuSlotId == null) return;
  const target = event.target as HTMLElement;
  if (menu.value?.contains(target) || target.closest?.('[data-room-slot]')) return;
  store.closeRoomMenu();
};

onMounted(() => document.addEventListener('pointerdown', closeOnOutsideClick));
onBeforeUnmount(() => document.removeEventListener('pointerdown', closeOnOutsideClick));
</script>
